import React, { useState, useCallback, ReactElement, useEffect } from 'react';
import * as css from './IndividualServices.module.scss';
import '../../css/themes.scss';
import { IconHelpCircle, IconHelpCircleAlt } from 'src/content/icons';
import IndividualServicePanel from 'src/components/IndividualServices/IndividualServicePanel/containers/IndividualServicePanel';
import { MaintenanceContract, SpecData, VehicleRec, WebOp, WebOpChecked, ascCtaInteractionInput, ascPageviewInput, ascElementConfigurationInput } from 'src/models';
import { IView, IconButton, ScrollBar } from '@framework-controls/core';
import { strings } from 'src/localization';
import { ascCtaInteraction, ascPageview, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import { ComponentMapIds } from '../map';
import classNames from 'classnames';

export interface IProps {
    specData?: SpecData;
    tabSwitch: (value) => void;
    items: WebOp[];
    itemServiceChecked: WebOpChecked[];
    gmDealerCanEarn: string | undefined;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    mainPaneViewOrder: IView[];
    locale: string;
    toggleOpsLangLits: (locale: string) => void;
    toggleItem: (id: string) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    theme: string;
    maintContracts: MaintenanceContract[];
}

const IndividualServices = (props: IProps) => {
    const [displayHelpMessage, setDisplayHelpMessage] = useState(false);
    const [displayMCMessage, setDisplayMCMessage] = useState(false);
    const [screenEntry, setScreenEntry] = useState(false);
    const [initLangLits, setInitLangLits] = useState(true);

    useEffect(() => {
        if (initLangLits && props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.ServiceTab) {
            setInitLangLits(false);
            props.toggleOpsLangLits(props.locale);
        }
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId !== ComponentMapIds.ServiceTab) {
            setInitLangLits(true);
        }
    }, [props.toggleOpsLangLits, props.locale, initLangLits, props.mainPaneViewOrder]);


    const onHelpClick = useCallback(() => {
        setDisplayHelpMessage(!displayHelpMessage);
    }, [displayHelpMessage]);

    let indivItems: ReactElement[] | null = null;
    indivItems = props.items.map(panel => <IndividualServicePanel
        id={panel.id}
        key={panel.id}
        theme={props.theme}
    />);

    const linkButtonClassName = classNames(
        css.linkButton,
        {
            [css.linkButtonADA]: props.theme === 'colorSensitive',
            [css.linkButtonBMW]: props.theme === 'bmw',
            [css.linkButtonVW]: props.theme === 'volkswagen',
            [css.linkButtonAcura]: props.theme === 'acura',
        },
    );

    let helpDisplay = css.helpDisplay;
    let helpDisplayMini = css.helpDisplayNone;
    if (props.theme === 'mini') {
        helpDisplay = css.helpDisplayNone;
        helpDisplayMini = css.helpDisplay;
    }

    const onClick = useCallback((e: any) => {
        props.tabSwitch('writeYourOwn');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.AddYourOwnIndServiceLink,
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'nav_tab',
            eventAction: 'click',
            eventActionResult: displayHelpMessage ? 'close' : 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.AddYourOwnIndServiceLink,
            elementType: 'popup',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: displayHelpMessage ? 'close' : 'open',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [props.tabSwitch, displayHelpMessage, props.vehicles, props.selectedVehicle]);

    const gmRewardsDisclaimer = props.gmDealerCanEarn === 'Y' ? (
        <div className={css.gmRewardsDisclaimer}>
            {strings.GMRewardsDisclaimerServc}
        </div>
    ) : null;

    useEffect(() => {
        if (!screenEntry) {
            props.addLogEntry('Screen Entry', 'Individual Services', '');
            setScreenEntry(true);
        }

        return () => {
            setScreenEntry(true);
        };
    }, [props.addLogEntry, screenEntry]);

    useEffect(() => {
        if (props.maintContracts) {
            props.maintContracts.forEach(contract => {
                if (contract.status === 'Active') {
                    setDisplayMCMessage(true);
                }
            });
        }
    }, [props.maintContracts]);

    useEffect(() => {
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.ServiceTab) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: props.items.length.toString(),
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        }
    }, [props.items, props.vehicles, props.selectedVehicle, props.mainPaneViewOrder]);

    const addYourOwn = useCallback((e: any) => {
        onHelpClick();
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.AddYourOwnIndService,
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'information_link',
            eventAction: 'click',
            eventActionResult: displayHelpMessage ? 'close' : 'open',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.AddYourOwnIndService,
            elementType: 'popup',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: displayHelpMessage ? 'close' : 'open',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [onHelpClick, displayHelpMessage, props.vehicles, props.selectedVehicle]);

    const questionStyles = classNames(
        css.question,
        {
            [css.questionBMW]: props.theme === 'bmw',
            [css.questionFord]: props.theme === 'ford',

        },
    );

    const questionMiniStyles = classNames(
        css.questionMini,
        {
            [css.questionMiniBMW]: props.theme === 'bmw',
            [css.questionMiniFord]: props.theme === 'ford',

        },
    );

    return (
        <ScrollBar scrollVertical>
            <div className={css.body}>
                {displayMCMessage ? <div className={css.maintContractMessage}>
                    {strings.MaintContractMessage}
                </div> : null}
                {props.specData?.showDiagnTool || props.specData?.showWrtYourOwn ? <span className={css.gridQuestion}>
                    <div className={helpDisplay}>
                        <IconButton
                            id='HelpBtn'
                            onClick={addYourOwn}
                            variant={'help'}>
                            <div className={questionStyles}>
                                <IconHelpCircle />
                                <div className={questionStyles}>{strings.AddYourOwnIndService}</div>
                            </div>
                        </IconButton>
                    </div>
                    <div id='helpMini' className={helpDisplayMini}>
                        <IconButton
                            id='HelpBtnMini'
                            onClick={addYourOwn}
                            variant={'help'}>
                            <div className={questionStyles}>
                                <IconHelpCircleAlt />
                                <div className={questionMiniStyles}>{strings.AddYourOwnIndService}</div>
                            </div>
                        </IconButton>
                    </div>
                </span>: null}
                {displayHelpMessage ?
                    <div style={{ display: 'block' }}>
                        <div>
                            <small>
                                {strings.AddYourOwnIndServiceDetails}
                            </small>
                        </div>
                        <div>
                            <small>
                                <button
                                    id={'AddOwnLink'}
                                    className={linkButtonClassName}
                                    onClick={onClick}>
                                    {strings.AddYourOwnIndServiceLink}
                                </button>
                            </small>
                        </div>
                    </div> :
                    null
                }
                {gmRewardsDisclaimer}
                <div className={css.serviceItems}>
                    {indivItems}
                </div>
            </div>
        </ScrollBar>
    );
};

export default IndividualServices;